import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, MessageSquare, Globe } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { language, toggleLanguage, t } = useLanguage();

  const navLinks = [
    { path: '/', label: { el: 'ΑΡΧΙΚΗ', en: 'HOME' } },
    { path: '/services', label: { el: 'ΥΠΗΡΕΣΙΕΣ', en: 'SERVICES' } },
    { path: '/products', label: { el: 'ΠΡΟΪΟΝΤΑ', en: 'PRODUCTS' } },
    { path: '/about', label: { el: 'ΕΠΙΚΟΙΝΩΝΙΑ', en: 'CONTACT' } },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass-effect border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <img
              src="https://customer-assets.emergentagent.com/job_e6cf0493-e8d0-4f07-a179-d2f65d4a4d25/artifacts/8xj5q8hd_uc.png"
              alt="PrimeTech Santorini"
              className="h-20 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-10">
            <div className="flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.path.slice(1) || 'home'}`}
                  className={`text-sm font-semibold tracking-wider transition-colors whitespace-nowrap ${
                    isActive(link.path)
                      ? 'text-orange-500'
                      : 'text-slate-700 hover:text-orange-500'
                  }`}
                >
                  {t(link.label)}
                </Link>
              ))}
            </div>

            <button
              onClick={toggleLanguage}
              data-testid={`language-${language}`}
              className="p-2 hover:bg-slate-100 rounded-md transition-colors flex items-center"
              title={language === 'el' ? 'Switch to English' : 'Αλλαγή σε Ελληνικά'}
            >
              <Globe className="w-5 h-5 text-slate-600" />
              <span className="ml-1 text-xs font-semibold">{language.toUpperCase()}</span>
            </button>

            <Link
              to="/chat"
              data-testid="nav-chat-btn"
              className="bg-orange-500 text-white px-6 py-3 font-bold tracking-wider hover:bg-orange-600 transition-colors flex items-center space-x-2 whitespace-nowrap"
            >
              <MessageSquare className="w-5 h-5" />
              <span>{t({ el: 'ΑΝΑΦΟΡΑ ΒΛΑΒΗΣ', en: 'REPORT ISSUE' })}</span>
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-toggle"
            className="md:hidden p-2 rounded-md text-slate-700 hover:bg-slate-100"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="md:hidden bg-white border-t border-slate-200" data-testid="mobile-menu">
          <div className="px-4 py-6 space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                data-testid={`mobile-nav-link-${link.path.slice(1) || 'home'}`}
                className={`block text-sm font-semibold tracking-wider transition-colors ${
                  isActive(link.path)
                    ? 'text-orange-500'
                    : 'text-slate-700 hover:text-orange-500'
                }`}
              >
                {t(link.label)}
              </Link>
            ))}
            
            <button
              onClick={toggleLanguage}
              data-testid="mobile-language-toggle"
              className="w-full text-left py-2 px-3 hover:bg-slate-100 rounded-md transition-colors flex items-center space-x-2"
            >
              <Globe className="w-5 h-5 text-slate-600" />
              <span className="text-sm font-semibold">
                {language === 'el' ? 'English' : 'Ελληνικά'}
              </span>
            </button>

            <Link
              to="/chat"
              onClick={() => setIsOpen(false)}
              data-testid="mobile-chat-btn"
              className="block w-full bg-orange-500 text-white px-6 py-3 text-center font-bold tracking-wider hover:bg-orange-600 transition-colors"
            >
              <MessageSquare className="w-5 h-5 inline-block mr-2" />
              {t({ el: 'ΑΝΑΦΟΡΑ ΒΛΑΒΗΣ', en: 'REPORT ISSUE' })}
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;